import { CODE_ABHOLUNG } from '@/hooks/useVersandarten';

/**
 * Irgendein Code, der NICHT Abholung ist. `bereitLabel` und
 * `abschlussLabel` unterscheiden nur diese eine Grenze — alles ausser
 * Abholung ist Versand —, deshalb genuegt ein Stellvertreter. Er steht
 * hier als Konstante und nicht als Zeichenkette an der Aufrufstelle,
 * damit niemand ihn fuer einen echten Versandart-Code haelt und danach
 * sucht.
 */
const CODE_VERSAND_BEISPIEL = '__versand__';

/**
 * Wie der Zustand `bereit` in dieser Bestellung heisst.
 *
 * NUR NOCH EINE LESART (A46). Bis hierher hiess derselbe Zustand bei
 * Abholung "Abholbereit" und bei Versand "Versandfertig" (E19/E20).
 * Der zweite Fall kann nicht mehr eintreten: Eine Versand-Bestellung
 * geht von "Eingetroffen" direkt nach `abgeschlossen`. Der Betreiber
 * woertlich: "Es passiert manchmal, dass ein Paket liegen bleibt, aber
 * wir werden nicht noch einen Klick machen, um den Status auf
 * ,Versendet` zu aktualisieren."
 *
 * Damit ist `bereit` fuer Versand unerreichbar — die Menues bieten ihn
 * nicht mehr an (`zustaendeFuerBestellung`), und der Server weist ihn
 * ab (`zustandErlaubtFuerBestellung` in api/lib/zustandsweg.php). Ein
 * Wort fuer einen unmoeglichen Zustand ist schlimmer als keines: Es
 * stuende in der Filterliste, in der Auswahl und in der Einfuehrung und
 * haette keinen Fall mehr, den es beschreibt.
 *
 * Ohne Versandart bleibt es beim neutralen "Bereit" — dann wissen wir
 * selbst nicht, welcher Weg gilt, und `bereit` ist dort weiter
 * erreichbar.
 */
export function bereitLabel(versandartCode: string | null | undefined): string {
  if (versandartCode === null || versandartCode === undefined) return 'Bereit';
  return versandartCode === CODE_ABHOLUNG ? 'Abholbereit' : 'Bereit';
}

/**
 * Wie der Endzustand `abgeschlossen` in dieser Bestellung heisst (A11).
 *
 * DERSELBE ZUSTAND, ZWEI ENDEN
 *   Der Weg ist asymmetrisch. Bei einer Abholung endet er, wenn die
 *   Kundin im Laden war — davor liegt „Abholbereit", und dazwischen
 *   liegt eine echte Wartezeit. Bei Versand endet er, sobald das Paket
 *   am Packtisch liegt: "Zwischen versandfertig und abgeschlossen machen
 *   wir keinen Unterschied." Seit A46 gilt das nicht mehr nur dem Sinn
 *   nach, sondern im Weg: Es gibt fuer Versand keinen Zwischenzustand.
 *
 *   Der Zustandscode bleibt in beiden Faellen `abgeschlossen`. Daran
 *   haengen die Archivierung (ist_endzustand), der berechnete
 *   Checklisten-Punkt, die Auswertung und der Shopify-Webhook. Ein
 *   zweiter Endzustand waere ein Wort wert und ein Umbau an fuenf
 *   Stellen teuer (E19/E20, fortgeschrieben).
 *
 *   Ohne Versandart bleibt es beim neutralen Wort — dann wissen wir
 *   selbst nicht, welche der beiden Aussagen gilt. Dieselbe Begruendung
 *   wie bei „Bereit".
 */
export function abschlussLabel(versandartCode: string | null | undefined): string {
  if (versandartCode === null || versandartCode === undefined) return 'Abgeschlossen';
  return versandartCode === CODE_ABHOLUNG ? 'Abgeholt' : 'Versendet';
}

/**
 * Wie `bereit` dort heisst, wo NICHT ueber eine einzelne Bestellung
 * gesprochen wird — im Filter des Bretts (A12.8).
 *
 * Hier standen beide Lesarten ("Abholbereit / Versandfertig"), weil
 * unter `bereit` Abholungen wie Versandvorgaenge lagen. Seit A46 liegen
 * dort nur noch Abholungen: Versand geht an `bereit` vorbei. Der
 * Schraegstrich benannte damit einen zweiten Fall, den es nicht gibt.
 */
export function bereitLabelBeide(): string {
  return bereitLabel(CODE_ABHOLUNG);
}

/**
 * Dasselbe fuer den Endzustand — hier bleiben es ZWEI Lesarten.
 *
 * Anders als bei `bereit`: In `abgeschlossen` landen weiterhin beide
 * Wege, die Abholung ueber „Abgeholt" und der Versand ueber
 * „Versendet". Der Schraegstrich steht also fuer zwei Faelle, die es
 * beide gibt.
 */
export function abschlussLabelBeide(): string {
  return `${abschlussLabel(CODE_ABHOLUNG)} / ${abschlussLabel(CODE_VERSAND_BEISPIEL)}`;
}

/**
 * Welchen Zustand setzt der Knopf „Alle eingetroffen …"? (A11.1)
 *
 * WARUM DER KNOPF BEI VERSAND WEITER SPRINGT
 *   Bei Abholung folgt auf „Abholbereit" noch ein Handgriff: die Kundin
 *   kommt, und jemand markiert abgeholt. Bei Versand folgt nichts mehr —
 *   die Bestellung geht zum Packtisch, und niemand fasst sie im Tracking
 *   wieder an. Der Zwischenschritt war deshalb kein Schritt, sondern ein
 *   Liegenbleiben: Versand-Bestellungen standen auf dem Zwischenzustand
 *   und blieben dort. Seit A46 gibt es ihn fuer Versand nicht mehr —
 *   diese Funktion setzte die Regel schon vorher richtig um, jetzt ist
 *   sie auch die einzige Moeglichkeit.
 *
 *   Ohne Versandart bleibt es bei `bereit`. Wir wissen dann nicht, ob
 *   noch jemand kommt — und ein Abschluss, der zu frueh kommt, nimmt der
 *   Arbeitsliste einen Vorgang weg, der noch Arbeit macht.
 */
export function bereitZielZustand(versandartCode: string | null | undefined): string {
  if (versandartCode === null || versandartCode === undefined) return 'bereit';
  return versandartCode === CODE_ABHOLUNG ? 'bereit' : 'abgeschlossen';
}

/**
 * Die Beschriftung des Knopfes, der von „Eingetroffen" weiterfuehrt —
 * derselbe Wortlaut wie der Zustand, den er setzt.
 *
 * Bei Versand setzt er `abgeschlossen`, und der heisst dort
 * "Versendet". Das Wort "versandfertig" ist mit A46 weggefallen: Es
 * benannte den Zwischenschritt, den dieser Knopf ueberspringt — und den
 * es seither gar nicht mehr gibt.
 */
export function bereitKnopfLabel(
  versandartCode: string | null | undefined,
  amPc = false
): string {
  if (versandartCode === null || versandartCode === undefined) {
    return 'Alle eingetroffen — bereit setzen';
  }
  if (versandartCode === CODE_ABHOLUNG) return 'Alle eingetroffen — abholbereit setzen';
  // Am PC wird die Rechnung geschrieben (02.11.2026): Der Knopf nennt
  // den Handgriff, nach dem er kommt. „Abschließen" allein klang nach
  // etwas für später — und wurde für später aufgehoben.
  return amPc ? 'Rechnung geschrieben — abschließen' : 'Alle eingetroffen — abschließen';
}

/**
 * Der anzuzeigende Zustandsname. `bereit` und `abgeschlossen` haengen von
 * der Versandart ab; alles andere kommt unveraendert aus der gepflegten
 * Tabelle.
 *
 * Diese eine Funktion ist der Ort fuer die Unterscheidung — ueberall, wo
 * ein Zustand erscheint, laeuft der Name hier durch. Ein zweiter
 * Mechanismus daneben liefe frueher oder spaeter auseinander, und dann
 * hiesse dieselbe Bestellung im Brett anders als in ihrer Ansicht.
 */
export function zustandAnzeige(
  code: string,
  label: string,
  versandartCode: string | null | undefined
): string {
  if (code === 'bereit') return bereitLabel(versandartCode);
  if (code === 'abgeschlossen') return abschlussLabel(versandartCode);
  return label;
}

/**
 * Welche Zustände für DIESE Bestellung überhaupt in Frage kommen (A46).
 *
 * Bis hierher listeten beide Menüs — der Chip in der Bestellung und die
 * Pille im Brett — die gepflegte Zustandsliste ungefiltert. Jede
 * Bestellung konnte in jeden Zustand, auch in einen, den es für sie
 * nicht gibt: `bereit` bei Versand.
 *
 * DIE REGEL STEHT HIER UND NICHT ZWEIMAL. Genau derselbe Grund, aus dem
 * `zustandAnzeige()` an einer Stelle steht: Zwei Menüs mit je eigener
 * Auswahl laufen auseinander, und dann bietet das Brett an, was die
 * Bestellung verweigert.
 *
 * MASSGEBLICH IST TROTZDEM DER SERVER. Diese Funktion nimmt dem Nutzer
 * die unmögliche Wahl aus der Liste; abgewiesen wird sie in
 * `zustandErlaubtFuerBestellung()` (api/lib/zustandsweg.php). Wer per
 * Hand POST schickt, kommt an dieser Funktion vorbei — an der anderen
 * nicht.
 *
 * Ohne Versandart bleibt `bereit` stehen: Dann ist noch offen, ob
 * abgeholt oder verschickt wird, und der Zustand ist erreichbar.
 */
export function zustaendeFuerBestellung<T extends { code: string }>(
  zustaende: readonly T[] | undefined,
  versandartCode: string | null | undefined
): T[] {
  const liste = zustaende ?? [];
  if (versandartCode === null || versandartCode === undefined) return [...liste];
  if (versandartCode === CODE_ABHOLUNG) return [...liste];
  return liste.filter((z) => z.code !== 'bereit');
}

/**
 * Wer schliesst diese Bestellung ab? (Konzeptpruefung 29.10.2026, E47)
 *
 *   'shopify'   Glomer-Bestellung mit Versand. Shopify fuehrt „ausgefuehrt";
 *               der Webhook orders/fulfilled schliesst hier ab (gemessen:
 *               27 von 30). Das Tracking bietet keinen eigenen Abschluss an,
 *               sondern den Weg nach Shopify.
 *   'abholung'  Abholung — auch eine Glomer-Abholung. Dass die Kundin im
 *               Laden war, weiss nur das Tracking: Knopf „Abgeholt".
 *   'versand'   Telefon, Mail, Laden mit Versand: das Tracking schliesst ab
 *               (Rechnung steht in BONUS, nicht in Shopify).
 *   'offen'     Keine Versandart — wir wissen es selbst nicht.
 */
export type AbschlussWeg = 'shopify' | 'abholung' | 'versand' | 'offen';

export function abschlussWeg(b: {
  shopify_order_id: string | null;
  versandart: { code: string } | null;
}): AbschlussWeg {
  const code = b.versandart?.code ?? null;
  if (code === CODE_ABHOLUNG) return 'abholung';
  if (b.shopify_order_id !== null) return 'shopify';
  return code === null ? 'offen' : 'versand';
}
